import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ArrowUpIcon, ArrowDownIcon, XIcon } from './Icons';

const NumberInput = ({
  value,
  onChange,
  name,
  placeholder = "Enter number",
  required = false,
  readOnly = false,
  disabled = false,
  className,
  min,
  max,
  step = 1,
  decimals,
  showClearButton = true,
  autoComplete,
  autoFocus = false,
  size = "default",
  variant = "default",
  error,
  helperText,
  prefix,
  suffix,
  showSteppers = true,
  allowNegative = true,
  thousandsSeparator = true,
  currency,
  percentage = false,
  ...props
}) => {
  const { theme } = useTheme();
  const [inputValue, setInputValue] = useState(value || '');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // Format number for display
  const formatNumber = (num) => {
    if (num === '' || num == null) return '';
    
    const numValue = parseFloat(num);
    if (isNaN(numValue)) return '';
    
    // Handle percentage
    if (percentage) {
      return `${numValue}%`;
    }
    
    // Handle currency
    if (currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: decimals || 2,
        maximumFractionDigits: decimals || 2
      }).format(numValue);
    }
    
    // Handle thousands separator and decimals
    if (thousandsSeparator) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals || 0,
        maximumFractionDigits: decimals || (Number.isInteger(numValue) ? 0 : 10)
      }).format(numValue);
    }
    
    // Handle decimals only
    if (decimals !== undefined) {
      return numValue.toFixed(decimals);
    }
    
    return numValue.toString();
  };

  // Parse display value back to number
  const parseDisplayValue = (displayValue) => {
    // Handle null, undefined, or empty values
    if (displayValue === null || displayValue === undefined || displayValue === '') return '';
    if (displayValue === 0) return '0';
    
    // Ensure displayValue is a string before calling replace
    const stringValue = typeof displayValue === 'string' ? displayValue : String(displayValue);
    
    // Additional safety check
    if (typeof stringValue.replace !== 'function') {
      console.error('NumberInput parseDisplayValue error: stringValue is not a string:', stringValue, typeof stringValue);
      return String(displayValue);
    }
    
    // Remove currency symbols, percentage signs, and thousand separators
    let cleanValue = stringValue
      .replace(/[$€£¥₹,\s%]/g, '')
      .replace(/[^\d.-]/g, '');
    
    // Handle negative numbers
    if (!allowNegative) {
      cleanValue = cleanValue.replace('-', '');
    }
    
    return cleanValue;
  };

  // Validate number constraints
  const validateNumber = (numValue) => {
    if (numValue === '' || numValue == null) return true;
    
    const num = parseFloat(numValue);
    if (isNaN(num)) return false;
    
    if (min !== undefined && num < min) return false;
    if (max !== undefined && num > max) return false;
    
    return true;
  };

  // Handle input change
  const handleInputChange = (event) => {
    let newValue = event.target.value;
    
    // Parse the value to remove formatting
    const parsedValue = parseDisplayValue(newValue);
    
    // Validate the number
    if (parsedValue !== '' && !validateNumber(parsedValue)) {
      return; // Don't update if invalid
    }
    
    setInputValue(parsedValue);
    
    if (onChange) {
      onChange({
        target: {
          name,
          value: parsedValue,
          type: 'number'
        }
      });
    }
  };

  // Handle blur formatting
  const handleBlur = (event) => {
    setIsFocused(false);
    
    // Format the number on blur if it's valid
    if (inputValue !== '' && validateNumber(inputValue)) {
      const formattedValue = formatNumber(inputValue);
      setInputValue(formattedValue);
    }
    
    if (props.onBlur) {
      props.onBlur(event);
    }
  };

  // Handle focus - remove formatting for editing
  const handleFocus = (event) => {
    setIsFocused(true);
    
    // Remove formatting when focused for easier editing
    const rawValue = parseDisplayValue(inputValue);
    setInputValue(rawValue);
    
    if (props.onFocus) {
      props.onFocus(event);
    }
  };

  // Handle stepper buttons
  const handleStep = (direction) => {
    if (disabled || readOnly) return;
    
    const currentValue = parseFloat(inputValue) || 0;
    const stepValue = parseFloat(step) || 1;
    const newValue = direction === 'up' ? currentValue + stepValue : currentValue - stepValue;
    
    // Check constraints
    if (min !== undefined && newValue < min) return;
    if (max !== undefined && newValue > max) return;
    if (!allowNegative && newValue < 0) return;
    
    const finalValue = decimals !== undefined ? newValue.toFixed(decimals) : newValue.toString();
    setInputValue(finalValue);
    
    if (onChange) {
      onChange({
        target: {
          name,
          value: finalValue,
          type: 'number'
        }
      });
    }
    
    // Focus the input after stepping
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle clear button
  const handleClear = (event) => {
    event.stopPropagation();
    setInputValue('');
    
    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
          type: 'number'
        }
      });
    }
    
    // Focus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Update input value when prop value changes
  useEffect(() => {
    const newValue = value || '';
    setInputValue(isFocused ? parseDisplayValue(newValue) : newValue);
  }, [value, isFocused]);

  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    default: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  // Variant styles
  const variantClasses = {
    default: `${theme.colors.surface} ${theme.text.primary} ${theme.border.light}`,
    filled: `bg-gray-100 dark:bg-gray-800 ${theme.text.primary} border-transparent`,
    outlined: `bg-transparent ${theme.text.primary} ${theme.border.default}`,
    underlined: `bg-transparent ${theme.text.primary} border-0 border-b-2 ${theme.border.light} rounded-none`
  };

  // Check if value is valid
  const isValid = inputValue === '' || validateNumber(parseDisplayValue(inputValue));

  return (
    <div className="relative">
      {/* Main input container */}
      <div className="relative">
        {/* Prefix */}
        {prefix && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none z-10">
            {prefix}
          </div>
        )}
        
        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={cn(
            'w-full border rounded-md transition-colors',
            sizeClasses[size],
            variantClasses[variant],
            prefix && 'pl-10',
            suffix && 'pr-10',
            showClearButton && (value || inputValue) && !disabled && !readOnly && 'pr-10',
            showSteppers && !disabled && !readOnly && 'pr-16',
            showClearButton && showSteppers && (value || inputValue) && !disabled && !readOnly && 'pr-20',
            isFocused && `${theme.border.focus} ring-2 ring-blue-500/20`,
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
            !isValid && 'border-red-500',
            disabled && 'opacity-50 cursor-not-allowed',
            readOnly && 'cursor-default bg-gray-50 dark:bg-gray-800',
            className
          )}
          {...props}
        />
        
        {/* Suffix */}
        {suffix && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
            {suffix}
          </div>
        )}
        
        {/* Stepper buttons */}
        {showSteppers && !disabled && !readOnly && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex flex-col">
            <button
              type="button"
              onClick={() => handleStep('up')}
              className="px-1 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              title="Increase value"
            >
              <ArrowUpIcon className="w-3 h-3 text-gray-500" />
            </button>
            <button
              type="button"
              onClick={() => handleStep('down')}
              className="px-1 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              title="Decrease value"
            >
              <ArrowDownIcon className="w-3 h-3 text-gray-500" />
            </button>
          </div>
        )}
        
        {/* Clear button */}
        {showClearButton && (value || inputValue) && !disabled && !readOnly && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              "absolute top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors",
              showSteppers ? 'right-12' : 'right-2'
            )}
            title="Clear value"
          >
            <XIcon className="w-4 h-4 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
          </button>
        )}
      </div>
      
      {/* Helper text and validation info */}
      <div className="mt-1 flex justify-between items-start">
        {/* Helper text or error */}
        <div className="flex-1">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
          {!error && helperText && (
            <div className={`text-sm ${theme.text.muted}`}>
              {helperText}
            </div>
          )}
        </div>
        
        {/* Range info */}
        {(min !== undefined || max !== undefined) && (
          <div className={`text-xs ml-2 ${theme.text.muted}`}>
            {min !== undefined && max !== undefined && `${min} - ${max}`}
            {min !== undefined && max === undefined && `Min: ${min}`}
            {min === undefined && max !== undefined && `Max: ${max}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberInput;
